import React, { useEffect, useMemo, useRef, useState } from "react"
import { DateTime } from "luxon"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { useInitialDateFilters } from "../../../../../shared/components/table/useInitialDateFilters"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { useAppSelector } from "../../../../store/storeHooks"
import { selectEffectiveCompanyId } from "../../../../context/contextSelectors"
import { fetchGuests, ServiceCenterGuestListQuery, ServiceCenterGuestListResponse } from "../guestListApi"
import { GuestInfo, GuestInfoState, GuestInfoType } from "../guestListTypes"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { StandardExportButton } from "../../../../../shared/components/table/StandardExportButton"
import { StandardDateRangeFilter } from "../../../../../shared/components/table/StandardDateRangeFilter"
import { useExportGuests } from "../hooks/useExportGuests"
import { StandardChip } from "../../../../../shared/components/badges/StandardChip"
import { ActionBarItem, FloatingActionBar } from "../../../../../shared/components/table/FloatingActionBar"
import { faFileArrowDown, faHourglass, faPrint } from "@fortawesome/pro-light-svg-icons"
import { useLabelPrinter } from "../../home/hooks/useLabelPrinter"
import { enqueueSnackbar } from "notistack"
import { Box, Tooltip, Typography } from "@mui/material"
import { useDateTime } from "../../../../localization/useDateTime"
import { GuestData } from "../../external/brotherPrinter"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Stack } from "@mui/system"

const selectGuests = (data: ServiceCenterGuestListResponse) => data?.guests || []

export function Guests() {
    const serviceCenterId = useAppSelector(selectEffectiveCompanyId)
    const dateTimeFactory = useDateTime()

    const { l10n } = useAppLocalization()
    const { startDate, endDate } = useInitialDateFilters()
    const [dateState, setDateState] = useState<{
        startDate: DateTime
        endDate: DateTime
    }>({
        startDate: startDate,
        endDate: endDate,
    })
    const [selectedRows, setSelectedRows] = useState<GuestInfo[]>()
    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const tableInstanceRef = useRef<MRT_TableInstance<GuestInfo>>(null)

    const { printMultipleGuestLabels, hasError, printErrorMessage } = useLabelPrinter()

    const guestsResult = useApiQuery(fetchGuests, {
        queryName: ServiceCenterGuestListQuery.SERVICE_CENTER_GUEST_LIST,
        dependencies: {
            serviceCenterId: serviceCenterId ?? 0,
            startDate: dateState.startDate.toISODate(),
            endDate: dateState.endDate.toISODate(),
        },
        select: selectGuests,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(guestsResult)

    const tooltip = (state: GuestInfoState, timeline: GuestInfo["timeline"]) => {
        switch (state) {
            case GuestInfoState.CHECKED_IN:
                return l10n.getString(
                    "service-center-reports-dashboard-guests-table-host-company-status-checked-in-hover-tooltip",
                )
            case GuestInfoState.CHECKED_OUT:
                return l10n.getString(
                    "service-center-reports-dashboard-guests-table-host-company-status-checked-out-hover-tooltip",
                )
            case GuestInfoState.PENDING:
                return l10n.getString(
                    "service-center-reports-dashboard-guests-table-host-company-status-pending-hover-tooltip",
                )
            case GuestInfoState.EXPECTED:
                return l10n.getString(
                    "service-center-reports-dashboard-guests-table-host-company-status-expected-hover-tooltip",
                )
            default:
                return ""
        }
    }

    const columns = useMemo<MRT_ColumnDef<GuestInfo>[]>(
        () => [
            columnFactory.shortNameColumn({
                header: l10n.getString("service-center-reports-dashboard-guests-table-check-in-time-title"),
                accessorKey: "timeline.checkInTime",
                Cell: ({ row }) => {
                    if (row.original.state === GuestInfoState.EXPECTED) {
                        const time = dateTimeFactory.fromApiOrUndefined(row.original.timeline.meetingTime)

                        if (!time) return null
                        return (
                            <Stack direction="row" alignItems="center" gap="0.5rem">
                                <Tooltip
                                    title={l10n.getString(
                                        "service-center-reports-dashboard-guests-table-check-in-time-tooltip",
                                    )}
                                >
                                    <FontAwesomeIcon icon={faHourglass} />
                                </Tooltip>
                                <Typography variant="body2">{dateTimeFactory.formatDateTime(time)}</Typography>
                            </Stack>
                        )
                    }
                    const time = dateTimeFactory.fromApiOrUndefined(row.original.timeline.checkInTime)

                    if (!time) return null
                    return dateTimeFactory.formatDateTime(time)
                },
            }),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("service-center-reports-dashboard-guests-table-check-out-time-title"),
                    accessorKey: "timeline.checkOutTime",
                },
                (row) => row.timeline.checkOutTime,
            ),
            columnFactory.shortNameColumn({
                header: l10n.getString("service-center-reports-dashboard-guests-table-guest-title"),
                accessorKey: "attendee",
                Cell: ({ row }) => {
                    const { attendee, type } = row.original

                    if (type === GuestInfoType.SINGLE) {
                        return <StandardTwoLinesCell title={attendee.name} subtitle1={attendee.company} />
                    }

                    return <StandardTwoLinesCell title="" subtitle1={attendee.company} />
                },
            }),
            columnFactory.shortNameColumn({
                header: l10n.getString("service-center-reports-dashboard-guests-table-host-title"),
                accessorKey: "host.displayName",
            }),
            columnFactory.statusColumn({
                header: l10n.getString("service-center-reports-dashboard-guests-table-host-company-status"),
                accessorKey: "state",
                Cell: ({ row }) => {
                    return (
                        <Tooltip title={tooltip(row?.original?.state, row?.original?.timeline)}>
                            <Box>
                                <StandardChip
                                    variant="info"
                                    placement="table"
                                    label={l10n.getStringForEnum(
                                        "service-center-reports-dashboard-guests-table-host-company-status",
                                        row.original.state,
                                        "Unknown",
                                    )}
                                />
                            </Box>
                        </Tooltip>
                    )
                },
                filterSelectOptions: [
                    {
                        value: GuestInfoState.CHECKED_IN,
                        text: l10n.getString(
                            "service-center-reports-dashboard-guests-table-host-company-status-checked-in",
                        ),
                    },
                    {
                        value: GuestInfoState.CHECKED_OUT,
                        text: l10n.getString(
                            "service-center-reports-dashboard-guests-table-host-company-status-checked-out",
                        ),
                    },
                    {
                        value: GuestInfoState.EXPECTED,
                        text: l10n.getString(
                            "service-center-reports-dashboard-guests-table-host-company-status-expected",
                        ),
                    },
                    {
                        value: GuestInfoState.PENDING,
                        text: l10n.getString(
                            "service-center-reports-dashboard-guests-table-host-company-status-pending",
                        ),
                    },
                ],
            }),
        ],
        [l10n, columnFactory],
    )

    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    useEffect(() => {
        if (hasError && printErrorMessage) {
            enqueueSnackbar({
                variant: "error",
                message: printErrorMessage,
            })
        }
    }, [hasError, printErrorMessage])

    const exportGuests = useExportGuests({
        data: selectedRows ? selectedRows : guestsResult.data,
        startDate: dateState.startDate || DateTime.now(),
        endDate: dateState.endDate || DateTime.now(),
    })

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    const handlePrintLabels = async () => {
        if (!selectedRows || selectedRows.length === 0) {
            return
        }

        const labelsToPrint: GuestData[] = selectedRows.map((guest) => ({
            visitorName: guest.type === GuestInfoType.SINGLE ? guest.attendee.name : "",
            hostName: guest.host.displayName,
            visitorCompany: guest.attendee.company,
        }))

        await printMultipleGuestLabels(labelsToPrint)
    }

    return (
        <>
            <ApiQueryTable
                tableInstanceRef={tableInstanceRef}
                columns={columns}
                state={{ rowSelection }}
                enableRowSelection
                onRowSelectionChange={setRowSelection}
                enableRowActions={false}
                initialState={{
                    density: "spacious",
                    sorting: [{ id: "timeline.meetingTime", desc: false }],
                    columnSizing: {
                        "timeline.checkInTime": 120,
                        "timeline.checkOutTime": 120,
                        attendee: 500,
                        "host.name": 500,
                        status: 100,
                    },
                }}
                renderToolbarInternalActions={({ table }) => (
                    <StandardTableFilter variant="right" table={table}>
                        <StandardExportButton onClick={() => exportGuests()} />
                    </StandardTableFilter>
                )}
                renderTopToolbarCustomActions={() => (
                    <StandardTableFilter variant="left">
                        <LocalizedStrict
                            id="service-center-reports-dashboard-guests-table-date-range-filter"
                            attrs={{ label: true }}
                        >
                            <StandardDateRangeFilter value={dateState} onChange={setDateState} />
                        </LocalizedStrict>
                    </StandardTableFilter>
                )}
            />
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="service-center-reports-dashboard-guests-table-floating-action-bar-guest-card"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem icon={faPrint} title="Guest card" onClick={() => handlePrintLabels()} />
                    </LocalizedStrict>

                    <LocalizedStrict
                        id="company-meeting-service-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem icon={faFileArrowDown} title="Export" onClick={() => exportGuests()} />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}
        </>
    )
}
